import {Injectable} from '@angular/core';
import {HttpService} from '../core/http.service';
import {Observable} from 'rxjs';
import {CdRai} from '../../interfaces/care/cd-rai/cd-rai';
import {CdRaiFactory} from '../../factories/care/cd-rai/cd-rai-factory';
import {CdMdsArea} from '../../interfaces/care/cd-rai/cd-mds-area';
import {CdMdsAreaFactory} from '../../factories/care/cd-rai/cd-mds-area-factory';
import {map} from 'rxjs/operators';
import {LightCustomer} from '../../interfaces/customers/light-customer';
import {cloneDeep} from 'lodash-es';
import {CdMdsAnswer} from '../../interfaces/care/cd-rai/cd-mds-answer';
import {CdMdsAnswerFactory} from '../../factories/care/cd-rai/cd-mds-answer-factory';
import {CdMdsAreasTable} from '../../tables/care/cd-mds-areas.table';
import {CdMdsAnswersTable} from '../../tables/care/cd-mds-answers.table';
import {CdRaisTable} from '../../tables/care/cd-rais.table';
import {AlertController} from '@ionic/angular';
import {db} from '../../tables/db.service';

@Injectable({
  providedIn: 'root'
})
export class RaisService {

  /**
   *
   * @param httpService
   * @param cdMdsAnswersTable
   * @param alertController
   * @param cdRaisTable
   * @param cdMdsAreasTable
   */
  constructor(private httpService: HttpService,
              private cdMdsAnswersTable: CdMdsAnswersTable,
              private alertController: AlertController,
              private cdRaisTable: CdRaisTable,
              private cdMdsAreasTable: CdMdsAreasTable) {
  }

  /**
   *
   * @param customerIds
   *
   */
  public loadLatestCdRai(customerIds: number[]): Observable<CdRai[]> {
    return this.httpService.get<any[]>({
      url: ['care', 'c-rais', 'latest-c-rais'],
      query: {
        customer_ids: customerIds.toString()
      }
    }).pipe(map((_caRais) => CdRaiFactory.fromArray(_caRais)));
  }

  /**
   *
   */
  public loadCdRaiMdsAreas(): Observable<CdMdsArea[]> {
    return this.httpService.get<any[]>({
      url: ['care', 'c-rais', 'get-c-mds-areas']
    }).pipe(map((_caRaiMdsAreas) => CdMdsAreaFactory.fromArray(_caRaiMdsAreas)));
  }

  /**
   *
   * @param customer
   * @param cdRai
   * @param cdRaiUuidToCopy
   */
  public async createCdRai(customer: LightCustomer, cdRai: CdRai, cdRaiUuidToCopy: string = null): Promise<{
    cdRai: CdRai,
    cdMdsAnswers: CdMdsAnswer[]
  }> {
    let newCdRai: CdRai = null;
    let cdMdsAnswers: CdMdsAnswer[];

    newCdRai = {...CdRaiFactory.new(), ...cdRai};
    newCdRai.customer = customer;
    newCdRai.customerId = customer.id;

    if(cdRaiUuidToCopy) {
      const cdMdsAnswersToCopy = await this.cdMdsAnswersTable.getByCdRaiUuid(cdRaiUuidToCopy);
      cdMdsAnswers = CdMdsAnswerFactory.copyAll(cdMdsAnswersToCopy);
    }
    else {
      cdMdsAnswers = await this.cdMdsAreasTable.getDefaultCdMdsAnswers();
    }

    await db.transaction('rw', db.cdMdsAnswers, db.cdRais, db.syncStatusItems, async () => {
      newCdRai = await this.cdRaisTable.add(newCdRai);
      for(const cdMdsAnswer of cdMdsAnswers) {
        cdMdsAnswer.cdRaiUuid = newCdRai.uuid;
      }
      await this.cdMdsAnswersTable.addMany(cdMdsAnswers, !cdRaiUuidToCopy);
    });

    return {
      cdRai: newCdRai,
      cdMdsAnswers: cdMdsAnswers
    };
  }

  /**
   *
   */
  public async clear(): Promise<void> {
    const isCdRaisTableSynced = await this.cdRaisTable.isSynced();
    const isCdMdsAnswersTableSynced = await this.cdMdsAnswersTable.isSynced();

    if(isCdRaisTableSynced && isCdMdsAnswersTableSynced) {
      await db.transaction('rw', db.cdMdsAnswers, db.cdRais, db.syncStatusItems, async () => {
        await this.cdRaisTable.clear();
        await this.cdMdsAnswersTable.clear();
      });
    }
  }

  /**
   *
   */
  public async setCdRaisToTable(cdRais: CdRai[]): Promise<void> {
    await this.clear();
    await this.addCdRaisToTable(cdRais);
  }

  /**
   *
   * @param cdRais
   */
  public async addCdRaisToTable(cdRais: CdRai[]): Promise<void> {
    const isCdRaisTableSynced =
      await this.cdRaisTable.isSyncedByUuids(
        cdRais.map(_cdRai => _cdRai.uuid)
      );

    let cdMdsAnswerUuids: string[] = [];
    for(const cdRai of cdRais) {
      cdMdsAnswerUuids = [
        ...cdMdsAnswerUuids,
        ...cdRai.cdMdsAnswers.map(_cdMdsAnswer => _cdMdsAnswer.uuid)
      ];
    }
    const isCdMdsAnswersTableSynced = await this.cdMdsAnswersTable.isSyncedByUuids(cdMdsAnswerUuids);

    if(isCdRaisTableSynced && isCdMdsAnswersTableSynced) {
      let cdMdsAnswers: CdMdsAnswer[] = [];
      for(const cdRai of cdRais) {
        cdMdsAnswers = [...cdMdsAnswers, ...cloneDeep(cdRai.cdMdsAnswers)];
        cdRai.cdMdsAnswers = [];
      }
      await db.transaction('rw', db.cdMdsAnswers, db.cdRais, db.syncStatusItems, async () => {
        await this.cdRaisTable.addOrUpdateMany(cdRais, true);
        await this.cdMdsAnswersTable.addOrUpdateMany(cdMdsAnswers, true);
      });
    }
    else {
      await this.showNotSyncedAlert();
    }
  }
  // service-facility/{service_facility_id}/set-finished/{c_care_plan_id}
  public setFinished(serviceFacilityId: number, cdCarePlanId: number): Observable<CdRai> {
    return this.httpService.put({
      url: ['care', 'service-facility', serviceFacilityId.toString(), 'set-finished', cdCarePlanId.toString()]
    }, {}).pipe(map(CdRaiFactory.fromObject));
  }

  /**
   *
   */
  private async showNotSyncedAlert(): Promise<void> {
    const notSyncedAlert = await this.alertController.create({
      header: 'Aktualisierung nicht möglich',
      cssClass: 'tk-alert',
      message: 'Es sind nicht alle RAI-Daten synchronisiert. Bitte den Status der Synchronisation überprüfen (Contextmenü -> Status).',
      buttons: ['OK']
    });
    await notSyncedAlert.present();
  }

}
